<zef-dialog
  [open]="state.open"
  [autoFocus]="false"
  [options]="{ restoreFocus: false, maxWidth: '1100px', width: '95vw' }"
  (closeTrigger)="onClose$.next()"
  (afterOpen)="onDialogOpened$.next()">
  <div class="__host __zcp-auth-host">

    <!-- Left column: controls -->
    <div class="__controls">

      <!-- Persistent header. Multi-agent walker (queue length > 1) shows
           the agent strip with per-agent bokeh glow (green = authed,
           blue = active). Single-agent collapses to the brand mark +
           agent name, with the same blue active bokeh since the lone
           agent is by definition the active one. -->
      <div class="__header">
        <h4 class="__heading">
          Authorize your agent{{ isSingleAgent() ? '' : 's' }}
          @if (state.serviceName) {
            for service
            @if (state.projectName) {
              <span class="c-internal-link">{{ state.projectName }}</span>/<span class="c-internal-link"><strong>{{ state.serviceName }}</strong></span>
            } @else {
              <span class="c-internal-link"><strong>{{ state.serviceName }}</strong></span>
            }
          } @else {
            in <code class="__heading-code">zcp</code>
          }
        </h4>

        @if (isSingleAgent()) {
          <div class="__single">
            <span class="__brand-wrap" aria-hidden="true">
              @switch (activeAgent()) {
                @case ('claude-code') {
                  <zui-claude-mark class="__brand" />
                }
                @case ('codex') {
                  <zui-codex-mark class="__brand" />
                }
                @case ('antigravity') {
                  <zui-antigravity-mark class="__brand" />
                }
                @case ('grok') {
                  <zui-grok-mark class="__brand" />
                }
                @case ('cursor') {
                  <zui-cursor-mark class="__brand" />
                }
              }
            </span>
            <span class="__single-name">{{ agentDisplayName() }}</span>
          </div>
        } @else {
          <zui-agent-strip class="__strip" [entries]="stripEntries()" />
        }
      </div>

      <!-- Scrollable middle. Holds the stepper + phase-specific text
           descriptions. Header, action pane, and footer are all pinned by
           the controls grid (auto / 1fr / auto / auto), so this is the ONLY
           thing that scrolls when the dialog runs out of vertical space.
           The paste-code/device-code token card lives in `__action-pane`
           below — it must stay above the fold so its input + Re-open/Copy
           URL buttons are never hidden behind the scroll. -->
      <zef-scrollbar class="__scroll" height="100%" showScrollButtonsV>

        <zui-separator
          class="__header-drop"
          orientation="vertical"
          size="small"
          spacing="disabled"
          position="start" />

        <!-- Vertical 5-step checklist -->
        <ol class="__stepper" aria-label="Authorization progress">
          @for (step of steps(); track $index) {
            <li class="__step"
                [class.__step--done]="$index < currentStepIndex()"
                [class.__step--active]="$index === currentStepIndex()"
                [class.__step--error]="$index === currentStepIndex() && isErrorActive()"
                [attr.aria-current]="$index === currentStepIndex() ? 'step' : null">
              <span class="__step-indicator" aria-hidden="true">
                @if ($index < currentStepIndex()) {
                  <mat-icon>check</mat-icon>
                } @else if ($index === currentStepIndex() && isErrorActive()) {
                  <mat-icon>close</mat-icon>
                } @else if ($index === currentStepIndex()) {
                  <span class="__step-dot"></span>
                } @else {
                  <span class="__step-dot __step-dot--pending"></span>
                }
              </span>
              <span class="__step-label">{{ step.label }}</span>
            </li>
          }
        </ol>

        <!-- Step detail: phase-specific copy. The paste-code / device-code
             token card is intentionally NOT rendered here — it sits in the
             pinned `__action-pane` below so its input + URL actions stay
             above the fold no matter how much vertical space the dialog has. -->
        <div class="__step-detail" aria-live="polite">

          @if (walkerComplete()) {
            <!-- Walker terminal state — summarize the run before the user
                 leaves for the control panel. Mixed outcomes (✓/✗/−) live
                 in the strip above; this block is just a single summary line. -->
            <p class="__desc">
              @if (hasAnySuccess()) {
                Authorization complete. Open the control plane to start
                working with your agent(s).
              } @else {
                No agents were authorized in this session. You can re-trigger
                authorization from the service card.
              }
            </p>
          } @else if (isErrorActive()) {
            <p class="__desc __desc--error">
              {{ errorMessage() || 'An error occurred during authorization.' }}
            </p>
          } @else if (authPhase() !== 'url_received' && authPhase() !== 'waiting_for_code') {
            @switch (authPhase()) {

              @case ('idle') {
                <p class="__desc">
                  Commands run securely on the isolated <code class="__desc-code">zcp</code>
                  container — any sensitive action will require your confirmation.
                  We use this to walk you through the OAuth process and initialize
                  the environment so it's ready to use.
                </p>
              }

              @case ('starting') {
                <p class="__desc">Initializing terminal session…</p>
              }

              @case ('confirming') {
                <p class="__desc">Confirming prompts automatically…</p>
              }

              @case ('submitting_code') {
                <p class="__desc">Submitting authorization code…</p>
              }

              @case ('complete') {
                <p class="__desc">
                  @if (isSingleAgent()) {
                    {{ agentDisplayName() }} has been successfully authorized.
                    You can now close this dialog.
                  } @else if (isLastAgent()) {
                    {{ agentDisplayName() }} authorized — wrapping up…
                  } @else {
                    {{ agentDisplayName() }} authorized — moving on to the next agent…
                  }
                </p>
              }

            }
          }

        </div>

      </zef-scrollbar>

      <!-- Pinned action pane. Renders the paste-code / device-code token
           card only during the URL-received / waiting-for-code phases.
           Sits between `__scroll` and `__footer` in the controls grid so
           the card and its action buttons always remain visible — never
           clipped behind the scroll like before. -->
      @if (authPhase() === 'url_received' || authPhase() === 'waiting_for_code') {
        <div class="__action-pane">
          @if (isPollFlow()) {
            <!-- Poll flows — the user finishes in their browser and the CLI
                 self-polls; we detect completion via authSuccessPattern and
                 auto-complete. No paste form here.
                 · device-code (Codex): terminal printed a code the user
                   types into the browser — shown in the code box below.
                 · url-poll (Cursor): nothing to type — the sign-in URL
                   carries everything; the user just approves. -->
            <div class="__token-card">
              @if (deviceCode()) {
                <p class="__desc">
                  Copy the code below, open the authorization page, and paste
                  it in your browser. We'll detect the completion automatically
                  — no need to come back here.
                </p>
              } @else {
                <p class="__desc">
                  A new tab opened with the authorization page. Approve the
                  sign-in there — we'll detect completion automatically, no
                  need to come back here.
                </p>
              }

              @if (deviceCode()) {
                <div class="__device-code">
                  <code class="__device-code-value">{{ deviceCode() }}</code>
                  <button
                    mat-stroked-button
                    type="button"
                    class="c-button--smaller __device-code-copy"
                    (click)="onCopyCode$.next()">
                    <mat-icon>{{ copiedCode() ? 'check' : 'content_copy' }}</mat-icon>
                    {{ copiedCode() ? 'Copied' : 'Copy Code' }}
                  </button>
                </div>
              }

              @if (authUrl()) {
                <div class="__device-url">{{ authUrl() }}</div>
              }

              <p class="__desc __desc--waiting">
                Waiting for browser confirmation…
              </p>
            </div>
          } @else {
            <!-- Paste-code flow (Claude). Browser shows a code, user pastes
                 it back here (or directly into the terminal pane on the
                 right). Form's (submit) handler keeps Enter-in-input
                 working; the Send Code button in the fixed footer fires
                 the same onSendCode$ subject. -->
            <div class="__token-card">
              <p class="__desc">
                @if (authPhase() === 'url_received') {
                  Open the authorization page, then paste the code you
                  receive — here or directly in the terminal.
                } @else {
                  A new tab opened with the authorization page. Paste the
                  code you receive — here or directly in the terminal.
                }
              </p>

              @if (authUrl()) {
                <div class="__url-actions">
                  <button
                    mat-stroked-button
                    type="button"
                    class="c-button--smaller __url-action"
                    (click)="onOpenUrl$.next()">
                    <mat-icon>open_in_new</mat-icon>
                    {{ authPhase() === 'url_received' ? 'Open' : 'Re-open' }}
                  </button>
                  <button
                    mat-stroked-button
                    type="button"
                    class="c-button--smaller __url-action"
                    (click)="onCopyUrl$.next()">
                    <mat-icon>{{ copied() ? 'check' : 'content_copy' }}</mat-icon>
                    {{ copied() ? 'Copied' : 'Copy URL' }}
                  </button>
                </div>
              }

              <zef-form (submit)="onSendCode$.next()">
                <zef-form-field class="__code-field">
                  <mat-form-field appearance="outline" color="accent">
                    <mat-label>Authorization Code</mat-label>
                    <zef-password-field>
                      <input
                        matInput
                        type="password"
                        name="auth-code"
                        autocomplete="one-time-code"
                        data-1p-ignore
                        data-lpignore="true"
                        placeholder="Paste code from browser"
                        [ngrxFormControlState]="state.formState?.controls.code"
                        [ngrxEnableFocusTracking]="true"
                        zefFormElement />
                    </zef-password-field>
                    <mat-hint>Pasted here or directly into the terminal — both work</mat-hint>
                  </mat-form-field>
                </zef-form-field>
              </zef-form>
            </div>
          }
        </div>
      }

      <!-- Fixed footer with the phase-specific primary action(s). Always
           rendered (no @if gate) so the footer track in the controls grid
           reserves vertical space regardless of phase, and pinned buttons
           never get displaced by tall middle content. The empty branch on
           intermediate `complete` keeps the slot but renders nothing —
           the strip + description carry the transition signal. -->
      <div class="__footer">
        @if (walkerComplete()) {
          <!-- Walker terminal state: the primary CTA wording matches the
               outcome. If at least one agent succeeded we route to the
               control panel; if all failed/skipped we just close the dialog
               (no point sending the user to a page they can't operate). -->
          @if (hasAnySuccess()) {
            <button
              mat-flat-button
              color="accent"
              class="c-button--full"
              (click)="onGoToControlPanel$.next()">
              Go to Control Panel
            </button>
          } @else {
            <!-- Outlined button — Close is the only CTA when walker terminates
                 with no successes, so it can't borrow visual hierarchy from a
                 peer (unlike `Dismiss` in idle, which sits next to a flat
                 primary). A standalone text-only button would read as
                 non-actionable; an outlined button stays visibly secondary
                 while keeping a clear hit target + hover affordance. -->
            <button
              mat-stroked-button
              class="c-button--full"
              (click)="onClose$.next()">
              Close
            </button>
          }
        } @else if (authPhase() === 'url_received' || authPhase() === 'waiting_for_code') {
          @if (isPollFlow()) {
            <!-- Poll flows (device-code / url-poll): Open is the user's
                 primary action (the CLI detects browser-side completion on
                 its own); Cancel aborts. -->
            @if (authUrl()) {
              <a
                mat-flat-button
                color="accent"
                class="c-button--full __device-open"
                [href]="authUrl()"
                target="_blank"
                rel="noopener noreferrer">
                <mat-icon>open_in_new</mat-icon>
                Open authorization page
              </a>
            }
            <button
              type="button"
              mat-button
              class="c-button--full __cancel-button"
              (click)="onCancel$.next()">
              Cancel
            </button>
          } @else {
            <!-- Paste-code: Send Code submits the form by firing the same
                 subject the form's (submit) handler does — Enter-in-input
                 keeps working through the form, the button works too. -->
            <button
              type="button"
              mat-flat-button
              color="accent"
              class="c-button--full __send-button"
              [disabled]="!state.formState?.isValid"
              (click)="onSendCode$.next()">
              Send Code
            </button>
            <button
              type="button"
              mat-button
              class="c-button--full __cancel-button"
              (click)="onCancel$.next()">
              Cancel
            </button>
          }
        } @else {
          @switch (authPhase()) {

            @case ('idle') {
              <button
                mat-flat-button
                color="accent"
                class="c-button--full"
                [disabled]="!terminalConnected()"
                (click)="onStartAuth$.next()">
                @if (terminalConnected()) {
                  Start Authorization
                } @else {
                  Waiting for container…
                }
              </button>
              <button
                mat-button
                class="c-button--full"
                (click)="onClose$.next()">
                Dismiss
              </button>
            }

            @case ('complete') {
              <!-- Intermediate per-agent complete state (multi-agent only).
                   Single-agent completes auto-close via the dispatcher.
                   No CTA during the 1s pause — strip + description carry
                   the "transitioning" signal. -->
            }

            @case ('error') {
              <button
                mat-flat-button
                color="accent"
                class="c-button--full"
                (click)="onRetry$.next()">
                Retry
              </button>
              @if (isSingleAgent()) {
                <button
                  mat-button
                  class="c-button--full __cancel-button"
                  (click)="onCancel$.next()">
                  Cancel
                </button>
              } @else {
                <button
                  mat-button
                  class="c-button--full __cancel-button"
                  (click)="onSkip$.next()">
                  Skip to {{ nextAgentName() ?? 'Complete' }}
                </button>
              }
            }

            @default {
              <button
                mat-button
                class="c-button--full __cancel-button"
                (click)="onCancel$.next()">
                Cancel
              </button>
            }

          }
        }
      </div>

    </div>

    <!-- Right column: terminal -->
    <div class="__terminal-wrapper">
      @if (state.serviceStackId && state.containerId && terminalReady()) {
        <z-terminal
          [serviceId]="state.serviceStackId"
          [containerId]="state.containerId"
          [showFilters]="false"
          scrollHeight="100%"
          (terminalOutput)="onTerminalOutput$.next($event)" />
      }
    </div>

    <!-- Close button -->
    <button
      type="button"
      class="__dialog-close-button"
      mat-icon-button
      (click)="onClose$.next()">
      <mat-icon>close</mat-icon>
    </button>

  </div>
</zef-dialog>
